// Payouts → Split — the pivot over the share ledger, and the river above it.
//
// Founder, 3 Sep: "nothing is viewable as it builds… no recon exists…
// it feels as if all is magic." This screen is the anti-magic instrument:
//
//   THE RIVER   where every rupee currently sits — accruing at brands →
//               settled awaiting the next split → this month's building
//               lines → opened as a statement → paid. Nothing ever "just
//               appears": you can watch it approach. (There is no confirm
//               step any more — the close files the tax invoice itself.)
//   THE PIVOT   the split by any breakdown — party, role, screen, premise,
//               day, month — with payable / house / gated side by side.
//               The server does every sum (billing-service, the one writer);
//               this screen never adds two numbers together.
//   THE RECON   the identities that make it not-magic, on the face of the
//               screen: lines == settled base to the paise, statements ==
//               consumed lines, close reallocations net to zero.

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHead, DataTable, Loading, ErrorState, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { inr, BASE, ROLE_LABEL, shaped } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

const DIMS = [
  { id: 'party', label: 'Party', help: 'Who the money belongs to — a fleet, or pDOOH (house).' },
  { id: 'role', label: 'Role', help: 'Premise operator, device owner, technical fees, margin.' },
  { id: 'screen', label: 'Screen', help: 'The physical device, by its field name.' },
  { id: 'premise', label: 'Premise', help: 'The cab node the screen rode in.' },
  { id: 'day', label: 'Day', help: 'IST play-day — the day the impressions actually showed.' },
  { id: 'month', label: 'Month', help: 'IST calendar month of the play-day.' },
  { id: 'disposition', label: 'Disposition', help: 'payable / house / gated — where each line stands.' },
];

const DIM_LABEL = Object.fromEntries(DIMS.map((d) => [d.id, d.label]));

export default function Split() {
  const [params, setParams] = useSearchParams();
  const dims = useMemo(
    () => (params.get('dims') || 'party,role').split(',').filter((d) => DIM_LABEL[d]).slice(0, 3),
    [params]);
  const range = { from: params.get('from') || '', to: params.get('to') || '' };

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };
  const toggleDim = (id) => {
    const next = dims.includes(id) ? dims.filter((d) => d !== id) : [...dims, id].slice(-3);
    setParam('dims', next.join(','));
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams({ dims: dims.join(',') });
    if (range.from) p.set('from', range.from);
    if (range.to) p.set('to', range.to);
    return p.toString();
  }, [dims, range.from, range.to]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/pivot?${qs}`).then((r) => shaped(r.data, 'recon')), [qs]);

  const head = (
    <PageHead
      title="The split"
      sub="Where the money goes, by any breakdown — and where every rupee currently sits on its way there. The server does every sum; this screen never adds two numbers."
      actions={<Button onClick={reload}>Refresh</Button>}
    />
  );

  if (loading) return <div className={s.wrap}>{head}<Loading what="the split" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reload} /></div>;

  const { rows, totals, recon, building, river, truncated } = data;
  const linesOk = Number(recon.lines_paise) === Number(recon.judged_base_paise) && Number(recon.invoices_off) === 0;
  const stmtsOk = Number(recon.statements_gross_paise) === Number(recon.statements_consumed_paise);
  const closeOk = Number(recon.close_net_paise) === 0;

  const columns = [
    ...dims.map((d, i) => ({
      key: `d${i}`, label: DIM_LABEL[d],
      title: DIMS.find((x) => x.id === d)?.help,
      mono: d === 'screen' || d === 'premise' || d === 'day',
      render: d === 'role'
        ? (r) => ROLE_LABEL[r[`d${i}`]] || r[`d${i}`]
        : d === 'disposition'
          ? (r) => ({ payable: 'payable', house: 'house', gated_residual: 'gated', rounding: 'rounding' }[r[`d${i}`]] || r[`d${i}`])
          : undefined,
    })),
    { key: 'total_paise', label: 'Total', align: 'num', title: 'Everything this slice carries — payable + house + gated.',
      sort: (r) => Number(r.total_paise), render: (r) => <strong>{inr(r.total_paise)}</strong> },
    { key: 'payable_paise', label: 'Payable', align: 'num', title: 'Owed to an external party — rolls into their statement at close.',
      sort: (r) => Number(r.payable_paise), render: (r) => inr(r.payable_paise) },
    { key: 'house_paise', label: 'House', align: 'num', title: 'pDOOH’s own lines — technical fees, margin, and shares of house-owned devices.',
      sort: (r) => Number(r.house_paise), render: (r) => inr(r.house_paise) },
    { key: 'gated_paise', label: 'Gated', align: 'num', title: 'Withheld by the day-gate. Day-by-day evidence lives on the Eligibility screen.',
      sort: (r) => Number(r.gated_paise), render: (r) => inr(r.gated_paise) },
    { key: 'invoices', label: 'Invoices', align: 'num', title: 'Distinct settled advertiser invoices feeding this slice.' },
  ];

  return (
    <div className={s.wrap}>
      {head}

      {/* THE RIVER — nothing "just appears" */}
      <div className={s.pipeline} title="Where every rupee currently sits on its way to a payout. Money only moves rightward.">
        <span className={s.pipeTitle}>The river</span>
        <span className={s.pipeItem} title="Delivered advertiser spend that has not settled into an invoice yet. It earns nobody anything until it settles — but you can watch it coming.">
          accruing at brands <b>{inr(river.accruing_paise, 0)}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        <span className={s.pipeItem} title="Settled invoices the sweep has not split yet — it runs each morning for invoices settled before that day (a settlement day must fully end before its days can be judged).">
          settled, splits next sweep <b>{river.settled_unswept_n}</b> · <b>{inr(river.settled_unswept_paise, 0)}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        <span className={s.pipeItem} title="This month's lines so far — the building numbers below.">
          building <b>{building.month}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        {/* 'drafted' is the river's name for OPEN statements — the field kept
            its key so the payload's shape did not change mid-deploy. The
            'confirmed' leg is gone with the act: it has always been ₹0 (no
            statement was ever confirmed) and now nothing can ever put a rupee
            in it, so rendering it would be a permanent zero pretending to be a
            stage. */}
        <span className={s.pipeItem} title="Open statements: what the close computed and nothing has paid.">
          open <b>{inr(river.drafted_paise, 0)}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        <span className={s.pipeItem}>paid <b>{inr(river.paid_paise, 0)}</b></span>
      </div>

      {/* AS IT BUILDS — the current month accumulating, day by day */}
      <div className={s.hero}>
        <div className={`${s.heroCard} ${s.heroPrimary}`}
          title="Payable lines written so far for this month's play-days. Grows with every sweep; opens a statement at close. It is the share ledger's working, not what we will pay — the payment is the tax invoice raised in each owner's name, on presence-verified delivery.">
          <span className={s.heroLabel}>Fleets accruing · {building.month}</span>
          <span className={s.heroValue}>{inr(building.payable_paise)}</span>
          {/* NOT "minus SIM and TDS" any more (founder, 24 Sep): SIM is recorded,
              not charged, and TDS is the accounts team's liability at transaction
              time, never a line on the document. And the close no longer derives
              what we owe from this at all — it raises each owner's tax invoice on
              presence-verified delivery, a different and smaller base. This is
              collection-side working, not a forecast of the payment. */}
          <span className={s.heroSub}>
            {building.days === 1 ? '1 play-day' : `${building.days} play-days`} split so far
            {' — '}collection-side working. What we owe is invoiced separately, on presence-verified delivery.
          </span>
        </div>
        <div className={s.heroCard} title="This month's gate-withheld money so far. Every gated day is listed with its trips and hours on Eligibility.">
          <span className={s.heroLabel}>Gate withheld · {building.month}</span>
          <span className={s.heroValue}>{inr(building.gated_paise)}</span>
          <span className={s.heroSub}>days under the bar, so far</span>
        </div>
        <div className={s.heroCard} title="pDOOH's own accrual this month: technical fees + margin + house-owned supply.">
          <span className={s.heroLabel}>pDOOH · {building.month}</span>
          <span className={s.heroValue}>{inr(building.house_paise)}</span>
          <span className={s.heroSub}>technical + margin + house-owned screens</span>
        </div>
      </div>

      {/* THE RECON — on the face of the screen, always global */}
      <div className={linesOk && stmtsOk && closeOk ? s.okBanner : s.badBanner}>
        <b>Reconciliation (whole ledger, live):</b>{' '}
        split lines {inr(recon.lines_paise)} {linesOk ? '=' : '≠'} settled base {inr(recon.judged_base_paise)} across {recon.invoices_shared} invoices
        {Number(recon.invoices_off) > 0 && <> · {recon.invoices_off} invoice(s) OFF</>}
        {/* THE IDENTITY STAYS; THE NUMBER COMES OFF THE GLASS. statements_gross
            is payout_statements.gross_share_paise — the founder's "legacy bull"
            (24 Sep), and this banner was the last surface still printing it.
            The identity is load-bearing (it is the close's only anti-double-
            count, and shareOracle's O4 pins gross to its consumed lines), so it
            is still checked on every load. A green check needs no figure; a
            broken one needs BOTH figures to debug, which is when they appear. */}
        {' · '}statements {stmtsOk
          ? 'reconcile to their consumed lines'
          : <>{inr(recon.statements_gross_paise)} ≠ consumed {inr(recon.statements_consumed_paise)}</>}
        {' · '}close reallocations net {closeOk ? 'to zero' : inr(recon.close_net_paise)}
        {' · '}frozen through <b>{recon.frozen_through || '—'}</b>, last sweep {recon.last_sweep_at ? new Date(recon.last_sweep_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}.
        {' '}These identities are re-checked on every load and nightly by the oracle — if this banner is green, nothing here is magic.
      </div>

      {/* THE PIVOT */}
      <div className={s.panel}>
        <div className={s.panelTitle}>Break it down by (click to add or remove; order = nesting)</div>
        <div className={s.presets}>
          {DIMS.map((d) => (
            <button key={d.id} type="button" title={d.help}
              className={`${s.preset} ${dims.includes(d.id) ? s.presetOn : ''}`}
              onClick={() => toggleDim(d.id)}>
              {dims.includes(d.id) ? `${dims.indexOf(d.id) + 1} · ${d.label}` : d.label}
            </button>
          ))}
        </div>
        <div className={s.formRow} style={{ marginBottom: 0 }}>
          <div className={s.field}>
            <label>From (play-day)</label>
            <input type="date" value={range.from} onChange={(e) => setParam('from', e.target.value)} />
          </div>
          <div className={s.field}>
            <label>To</label>
            <input type="date" value={range.to} onChange={(e) => setParam('to', e.target.value)} />
          </div>
          <span className={s.hint}>Blank dates = all history. The URL carries the whole view — copy it to share exactly this slice.</span>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        getKey={(r) => dims.map((_, i) => r[`d${i}`]).join('|')}
        filterOn={dims.map((_, i) => `d${i}`)}
        filterPlaceholder="Filter rows…"
        initialSort={{ key: 'total_paise', dir: 'desc' }}
        right={totals && (
          <span className={s.hint}>
            window total <b>{inr(totals.total_paise)}</b> · payable {inr(totals.payable_paise)} · house {inr(totals.house_paise)} · gated {inr(totals.gated_paise)}
            {truncated && ' · TRUNCATED at 5,000 rows — narrow the window'}
          </span>
        )}
      />

      <div style={{ height: 14 }} />
      <Explainer terms={['river', 'settled', 'waterfall', 'payable', 'gate', 'house', 'premise', 'deviceOwner', 'recon']} />
    </div>
  );
}
